import { useEffect } from "react";
import PageMeta from "../components/seo/PageMeta";
import {
  CV_ACHIEVEMENTS,
  CV_EDUCATION,
  CV_EXPERIENCE,
  CV_INTRO,
  CV_META,
  CV_PROFILE,
  CV_SKILLS,
} from "../data/cvContent";
import { buildPersonJsonLd, buildWebPageJsonLd } from "../lib/seo";
import { loomEmbedUrl, parseLoomShareId } from "../lib/loom";
import { vimeoEmbedUrl, vimeoWatchUrl } from "../lib/vimeo";
import { youtubeEmbedUrl, parseYoutubeVideoId, youtubeWatchUrl } from "../lib/youtube";
import "../styles/cv.css";

function ExternalLink({ href, children, showArrow = true }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="cv-link">
      {children}
      {showArrow ? <span aria-hidden="true"> ↗</span> : null}
    </a>
  );
}

/**
 * @param {{
 *   label: string,
 *   href: string,
 *   embedSrc: string,
 *   externalLabel: string,
 * }} props
 */
function RoleMediaEmbed({ label, href, embedSrc, externalLabel }) {
  return (
    <figure className="cv-role__media">
      <div className="cv-role__media-header">
        <span className="cv-role__media-label">{label}</span>
        <ExternalLink href={href}>{externalLabel}</ExternalLink>
      </div>
      <div className="cv-role__media-frame">
        <iframe
          src={embedSrc}
          title={label}
          allow="autoplay; fullscreen; picture-in-picture"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          loading="lazy"
        />
      </div>
    </figure>
  );
}

/** @param {{ link: import("../data/cvContent").CvRoleLink }} props */
function RoleLink({ link }) {
  if (link.embed === "loom") {
    const shareId = parseLoomShareId(link.href);
    if (!shareId) {
      return (
        <p className="cv-role__link">
          <ExternalLink href={link.href}>{link.label}</ExternalLink>
        </p>
      );
    }

    return (
      <RoleMediaEmbed
        label={link.label}
        href={link.href}
        embedSrc={loomEmbedUrl(shareId)}
        externalLabel="Open in Loom"
      />
    );
  }

  if (link.embed === "vimeo") {
    const embedSrc = vimeoEmbedUrl(link.href);
    if (!embedSrc) {
      return (
        <p className="cv-role__link">
          <ExternalLink href={link.href}>{link.label}</ExternalLink>
        </p>
      );
    }

    return (
      <RoleMediaEmbed
        label={link.label}
        href={vimeoWatchUrl(link.href)}
        embedSrc={embedSrc}
        externalLabel="Open in Vimeo"
      />
    );
  }

  if (link.embed === "youtube") {
    const videoId = parseYoutubeVideoId(link.href);
    if (!videoId) {
      return (
        <p className="cv-role__link">
          <ExternalLink href={link.href}>{link.label}</ExternalLink>
        </p>
      );
    }

    return (
      <RoleMediaEmbed
        label={link.label}
        href={youtubeWatchUrl(videoId)}
        embedSrc={youtubeEmbedUrl(videoId)}
        externalLabel="Open in YouTube"
      />
    );
  }

  return (
    <p className="cv-role__link">
      <ExternalLink href={link.href}>{link.label}</ExternalLink>
    </p>
  );
}

export default function CvPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <article className="cv-page" aria-label="CV">
      <PageMeta
        title={CV_META.title}
        description={CV_META.description}
        path={CV_META.path}
        image={CV_META.ogImage}
        imageAlt={CV_META.ogImageAlt}
        imageWidth={CV_META.ogImageWidth}
        imageHeight={CV_META.ogImageHeight}
        imageType={CV_META.imageType}
        twitterImage={CV_META.twitterImage}
        twitterImageWidth={CV_META.twitterImageWidth}
        twitterImageHeight={CV_META.twitterImageHeight}
        type="profile"
        keywords={[
          "CV",
          "resume",
          "CJ Jayamaha",
          "product manager",
          "product leader",
          "fintech",
          "SaaS",
          "AI",
        ]}
        jsonLd={[
          buildPersonJsonLd(),
          buildWebPageJsonLd({
            title: CV_META.title,
            description: CV_META.description,
            path: CV_META.path,
          }),
        ]}
      />

      <header className="cv-page__hero">
        <div className="cv-page__hero-inner">
          <p className="cv-page__eyebrow">PRODUCT LEADER · CV</p>
          <h1 className="cv-page__name">{CV_PROFILE.name}</h1>
          <p className="cv-page__tagline">{CV_PROFILE.tagline}</p>
          <ul className="cv-page__meta">
            <li>{CV_PROFILE.locations}</li>
            <li>{CV_PROFILE.nationality}</li>
          </ul>
        </div>
      </header>

      <div className="cv-page__layout">
        <aside className="cv-sidebar">
          <section className="cv-sidebar__block">
            <h2 className="cv-sidebar__label">Contact</h2>
            <ul className="cv-sidebar__contact">
              <li>
                <a href={`mailto:${CV_PROFILE.email}`}>{CV_PROFILE.email}</a>
              </li>
              <li>
                <a href={`tel:${CV_PROFILE.phoneUk.replace(/\s/g, "")}`}>
                  {CV_PROFILE.phoneUk}
                </a>
                <span className="cv-sidebar__contact-note">UK</span>
              </li>
              <li>
                <a href={`tel:${CV_PROFILE.phonePt.replace(/\s/g, "")}`}>
                  {CV_PROFILE.phonePt}
                </a>
                <span className="cv-sidebar__contact-note">Portugal</span>
              </li>
              <li>
                <ExternalLink href={CV_PROFILE.linkedin}>LinkedIn</ExternalLink>
              </li>
            </ul>
          </section>

          <section className="cv-sidebar__block">
            <h2 className="cv-sidebar__label">Core Skills</h2>
            <ul className="cv-skills">
              {CV_SKILLS.map((skill) => (
                <li key={skill} className="cv-skills__item">
                  {skill}
                </li>
              ))}
            </ul>
          </section>

          <section className="cv-sidebar__block">
            <h2 className="cv-sidebar__label">Education</h2>
            <div className="cv-education">
              <p className="cv-education__institution">{CV_EDUCATION.institution}</p>
              <p className="cv-education__qualification">{CV_EDUCATION.qualification}</p>
              <p className="cv-education__detail">
                {CV_EDUCATION.grade} · {CV_EDUCATION.certification}
              </p>
            </div>
          </section>
        </aside>

        <div className="cv-main">
          <section className="cv-section cv-intro" aria-labelledby="cv-intro-heading">
            <div className="cv-section__header">
              <h2 id="cv-intro-heading" className="cv-section__title">
                {CV_INTRO.title}
              </h2>
            </div>
            <div className="cv-intro__grid">
              <div className="cv-intro__video-wrap">
                <div className="cv-intro__video">
                  <iframe
                    src={youtubeEmbedUrl(CV_INTRO.videoId)}
                    title={CV_INTRO.videoCaption}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
                <p className="cv-intro__video-caption">{CV_INTRO.videoCaption}</p>
              </div>
              <div className="cv-intro__copy">
                {CV_INTRO.paragraphs.map((paragraph) => (
                  <p key={paragraph} className="cv-intro__paragraph">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </section>

          <section className="cv-section" aria-labelledby="cv-achievements-heading">
            <div className="cv-section__header">
              <h2 id="cv-achievements-heading" className="cv-section__title">
                Key Achievements
              </h2>
            </div>
            <div className="cv-achievements">
              {CV_ACHIEVEMENTS.map((item) => (
                <article key={item.id} className="cv-achievement">
                  <div className="cv-achievement__metric">{item.metric}</div>
                  <h3 className="cv-achievement__headline">{item.headline}</h3>
                  <p className="cv-achievement__detail">{item.detail}</p>
                  {item.link ? (
                    <ExternalLink href={item.link.href}>{item.link.label}</ExternalLink>
                  ) : null}
                </article>
              ))}
            </div>
          </section>

          <section className="cv-section" aria-labelledby="cv-experience-heading">
            <div className="cv-section__header">
              <h2 id="cv-experience-heading" className="cv-section__title">
                Professional Experience
              </h2>
            </div>
            <ol className="cv-timeline">
              {CV_EXPERIENCE.map((role) => (
                <li key={`${role.company}-${role.period}`} className="cv-role">
                  <div className="cv-role__marker" aria-hidden="true" />
                  <div className="cv-role__content">
                    <div className="cv-role__header">
                      <div>
                        <h3 className="cv-role__title">{role.role}</h3>
                        <p className="cv-role__company">
                          {role.company}
                          <span className="cv-role__location"> · {role.location}</span>
                        </p>
                      </div>
                      <div className="cv-role__dates">
                        <time dateTime={role.period.replace(/–/g, "-")}>{role.period}</time>
                        <span className="cv-role__arrangement">{role.arrangement}</span>
                      </div>
                    </div>
                    {role.summary ? <p className="cv-role__summary">{role.summary}</p> : null}
                    {role.bullets?.length ? (
                      <ul className="cv-role__bullets">
                        {role.bullets.map((bullet) => (
                          <li key={bullet}>{bullet}</li>
                        ))}
                      </ul>
                    ) : null}
                    {role.link ? <RoleLink link={role.link} /> : null}
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>
      </div>
    </article>
  );
}
